// client/src/components/admin/AssetManagement.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAssetActions } from "../../utils/rbacHelper";

// ✅ MUI
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  CircularProgress,
  useMediaQuery,
  Checkbox
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

// ✅ MUI DataGrid
import { DataGrid } from "@mui/x-data-grid";

// lucide icons
import {
  Download,
  Upload,
  Edit,
  Trash2,
  RotateCcw,
  Search,
  X,
  ChevronDown,
  Filter,
  AlertTriangle,
  Info,
  Smartphone,
  CheckSquare,
  Square,
  ExternalLink,
} from "lucide-react";

function AssetManagement({
  assetsData = [],
  isAssetsLoading,
  assetError,
  handleDownloadTemplate,
  handleBulkFileChange,
  handleBulkUpload,
  handleExportToExcel,
  setEditingAsset,
  setShowEditAssetModal,
  handleRestoreAsset,
  handlePermanentDeleteAsset,
  handleDeleteAsset,
  bulkFile,
  uploading,
  currentUserRole,
}) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const fileInputRef = useRef(null);

  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // selection
  const [selectedAssets, setSelectedAssets] = useState([]); // _id list
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);

  // bulk actions dropdown
  const [showBulkActions, setShowBulkActions] = useState(false);
  const bulkMenuRef = useRef(null);

  // upload modal
  const [uploadRequirements, setUploadRequirements] = useState([]);
  const [showUploadValidation, setShowUploadValidation] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // RBAC
  const assetActions = getAssetActions(currentUserRole);

  // close bulk menu on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) {
        setShowBulkActions(false);
      }
    };
    if (showBulkActions) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showBulkActions]);

  // Filter Logic (same as old)
  const filteredAssets = useMemo(() => {
    const q = (assetSearchQuery || "").toLowerCase();
    return (assetsData || []).filter((asset) => {
      const matchesSearch =
        asset?.IMEI?.toLowerCase().includes(q) ||
        asset?.model?.toLowerCase().includes(q) ||
        asset?.serialNumber?.toLowerCase().includes(q);

      const status = (asset?.status || "").toLowerCase();
      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "active" && (status === "active" || status === "online")) ||
        (filterStatus === "inactive" && (status === "inactive" || status === "offline")) ||
        (filterStatus === "deleted" && status === "deleted");

      return matchesSearch && matchesStatus;
    });
  }, [assetsData, assetSearchQuery, filterStatus]);

  // reset selection when search/filter changes
  useEffect(() => {
    setSelectedAssets([]);
    setSelectAllFiltered(false);
  }, [assetSearchQuery, filterStatus]);

  const filteredIds = useMemo(() => filteredAssets.map((a) => a._id), [filteredAssets]);
  const selectedCount = selectedAssets.length;
  const filteredCount = filteredAssets.length;
  const allFilteredSelected = filteredCount > 0 && selectedCount === filteredCount;

  // ✅ status chip
  const statusChip = (statusRaw) => {
    const s = (statusRaw || "unknown").toLowerCase();
    if (s === "active" || s === "online") return <Chip size="small" color="success" label={s.toUpperCase()} sx={{ fontWeight: 900, borderRadius: 2 }} />;
    if (s === "deleted") return <Chip size="small" color="error" label="DELETED" sx={{ fontWeight: 900, borderRadius: 2 }} />;
    if (s === "inactive" || s === "offline") return <Chip size="small" color="warning" label={s.toUpperCase()} sx={{ fontWeight: 900, borderRadius: 2 }} />;
    return <Chip size="small" color="default" label={String(statusRaw || "UNKNOWN").toUpperCase()} sx={{ fontWeight: 900, borderRadius: 2 }} />;
  };

  // CSV download selected (same as old)
  const convertToCSV = (data) => {
    const headers = ["IMEI", "Model", "Serial Number", "Type", "Status", "Purchase Date", "Provider", "MSDN"];
    const rows = data.map((asset) => [
      asset?.IMEI || "",
      asset?.model || "",
      asset?.serialNumber || "",
      asset?.type || "",
      asset?.status || "",
      asset?.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : "",
      asset?.simCard?.provider || "N/A",
      asset?.simCard?.MSDN || "No Number",
    ]);
    return [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
  };

  const handleDownloadSelected = () => {
    const selectedData = (assetsData || []).filter((a) => selectedAssets.includes(a._id));
    if (selectedData.length === 0) {
      alert("Please select at least one asset to download.");
      return;
    }
    const csvContent = convertToCSV(selectedData);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `selected-assets-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowBulkActions(false);
  };

  const handleSelectAllFiltered = () => {
    if (selectAllFiltered || allFilteredSelected) {
      setSelectedAssets([]);
      setSelectAllFiltered(false);
      return;
    }
    setSelectedAssets(filteredIds);
    setSelectAllFiltered(true);
  };

  // ✅ enhanced upload (same)
  const handleEnhancedBulkUpload = async () => {
    if (!bulkFile) {
      alert("Please select a file first.");
      return;
    }

    const validExtensions = [".xlsx", ".xls", ".csv"];
    const fileExt = bulkFile.name.substring(bulkFile.name.lastIndexOf(".")).toLowerCase();

    if (!validExtensions.includes(fileExt)) {
      setUploadRequirements([
        "❌ Invalid file format. Please upload .xlsx, .xls, or .csv files.",
        "✅ Required columns: IMEI, Model, Serial Number, Type, Status, Purchase Date",
        "✅ Purchase Date format: YYYY-MM-DD or MM/DD/YYYY",
        "✅ Status must be one of: active, inactive, online, offline, deleted",
      ]);
      setShowUploadValidation(true);
      return;
    }

    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => (prev >= 90 ? 90 : prev + 10));
    }, 200);

    try {
      await handleBulkUpload();
      clearInterval(interval);
      setUploadProgress(100);
      setTimeout(() => {
        setUploadProgress(0);
        // ✅ FIX: null safe
        if (fileInputRef.current) fileInputRef.current.value = "";
      }, 900);
    } catch (error) {
      clearInterval(interval);
      setUploadProgress(0);
      setUploadRequirements([
        "❌ Upload failed: " + (error?.message || "Unknown error"),
        "✅ Please check the file format and try again.",
        "✅ Make sure all required columns are present.",
        "✅ Ensure data types are correct.",
      ]);
      setShowUploadValidation(true);
    }
  };

  // ✅ DataGrid rows (IMPORTANT: id must always exist)
  const rows = useMemo(() => {
    return filteredAssets.map((a) => ({
      id: a?._id, // DataGrid internal id
      ...a,
    }));
  }, [filteredAssets]);

  // ✅ Columns (IMPORTANT: NO valueGetter that reads params.value)
  const columns = useMemo(() => {
    return [
      {
        field: "select",
        headerName: "",
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Tooltip title="Select/Deselect all filtered results">
            <span>
              <IconButton size="small" onClick={handleSelectAllFiltered}>
                {allFilteredSelected ? <CheckSquare size={18} /> : selectedAssets.length > 0 ? <CheckSquare size={18} /> : <Square size={18} />}
              </IconButton>
            </span>
          </Tooltip>
        ),
        renderCell: (params) => {
          const _id = params?.row?._id;
          const checked = _id ? selectedAssets.includes(_id) : false;
          return (
            <Checkbox
              size="small"
              checked={checked}
              onChange={() => {
                if (!_id) return;
                setSelectedAssets((prev) => (prev.includes(_id) ? prev.filter((x) => x !== _id) : [...prev, _id]));
              }}
            />
          );
        },
      },
      {
        field: "device",
        headerName: "Device Details",
        minWidth: 280,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1.2} sx={{ py: 1, width: "100%" }} alignItems="flex-start">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                background: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                flexShrink: 0,
              }}
            >
              <Smartphone size={18} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 1000, lineHeight: 1.2 }} noWrap title={params?.row?.model || ""}>
                {params?.row?.model || "Unknown Model"}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }} noWrap>
                IMEI: {params?.row?.IMEI || "-"}
              </Typography>
              {params?.row?.serialNumber ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
                  SN: {params.row.serialNumber}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        ),
      },
      {
        field: "type",
        headerName: "Type",
        width: 140,
        renderCell: (params) => (
          <Chip size="small" label={String(params?.row?.type || "Hardwired").toUpperCase()} sx={{ borderRadius: 2, fontWeight: 900 }} />
        ),
      },
      {
        field: "connectivity",
        headerName: "Connectivity",
        width: 220,
        sortable: false,
        renderCell: (params) => (
          <Stack spacing={0.2} sx={{ py: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>{params?.row?.simCard?.provider || "N/A"}</Typography>
            <Typography variant="caption" color="text.secondary">
              MSDN: {params?.row?.simCard?.MSDN || "No Number"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        renderCell: (params) => statusChip(params?.row?.status),
      },
      {
        field: "registrationState",
        headerName: "Registry",
        width: 170,
        renderCell: (params) => {
          const isRegistered = params?.row?.registrationState === "registered";
          return (
            <Chip
              size="small"
              label={isRegistered ? "REGISTERED DEVICE" : "ASSET ONLY"}
              color={isRegistered ? "info" : "default"}
              sx={{ fontWeight: 900, borderRadius: 2 }}
            />
          );
        },
      },
      {
        field: "purchaseDate",
        headerName: "Purchased On",
        width: 170,
        // ✅ FIX: valueGetter removed completely
        renderCell: (params) => (
          <Typography sx={{ py: 1 }} color="text.secondary">
            {params?.row?.purchaseDate ? new Date(params.row.purchaseDate).toLocaleDateString() : "-"}
          </Typography>
        ),
      },
      ...(assetActions.canEdit || assetActions.canDelete || assetActions.canRestore
        ? [
            {
              field: "actions",
              headerName: "Actions",
              width: 190,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderCell: (params) => {
                const asset = params?.row || {};
                const isDeleted = String(asset.status || "").toLowerCase() === "deleted";

                return (
                  <Stack direction="row" spacing={0.6} sx={{ py: 0.6 }}>
                    {!isDeleted && assetActions.canEdit && (
                      <Tooltip title="Edit asset">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingAsset(asset);
                            setShowEditAssetModal(true);
                          }}
                        >
                          <Edit size={16} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {isDeleted ? (
                      <>
                        {assetActions.canRestore && (
                          <Tooltip title="Restore asset">
                            <IconButton size="small" onClick={() => handleRestoreAsset(asset._id)}>
                              <RotateCcw size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {assetActions.canDelete && (
                          <Tooltip title="Permanent Delete">
                            <IconButton size="small" color="error" onClick={() => handlePermanentDeleteAsset(asset._id)}>
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      assetActions.canDelete && (
                        <Tooltip title={asset?.linkedDevice ? "Registered device exists. Handle device separately first." : "Delete asset"}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteAsset(asset._id)}
                              disabled={Boolean(asset?.linkedDevice)}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )
                    )}
                  </Stack>
                );
              },
            },
          ]
        : []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetActions, selectedAssets, allFilteredSelected, filteredIds, filteredAssets]);

  return (
    <Box sx={{ p: { xs: 1.2, md: 2.5 } }}>
      {/* HEADER */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.6, md: 2.2 },
          borderRadius: 4,
          borderColor: alpha(theme.palette.divider, 0.9),
          mb: 2,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} justifyContent="space-between">
          <Box>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 20, md: 26 } }}>Asset Inventory</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Pre-device hardware stock. Registered live trackers are handled separately in Device Management.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <TextField
              size="small"
              placeholder="Search by IMEI, Model, Serial..."
              value={assetSearchQuery}
              onChange={(e) => setAssetSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center", mr: 1, color: "text.secondary" }}>
                    <Search size={18} />
                  </Box>
                ),
                endAdornment: assetSearchQuery ? (
                  <IconButton size="small" onClick={() => setAssetSearchQuery("")}>
                    <X size={16} />
                  </IconButton>
                ) : null,
              }}
              sx={{ minWidth: { xs: "100%", sm: 280 } }}
            />

            <TextField
              select
              size="small"
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 200 } }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center", mr: 1, color: "text.secondary" }}>
                    <Filter size={16} />
                  </Box>
                ),
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active/Online</MenuItem>
              <MenuItem value="inactive">Inactive/Offline</MenuItem>
              <MenuItem value="deleted">Deleted</MenuItem>
            </TextField>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* ACTION TOOLBAR */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ md: "center" }} justifyContent="space-between">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Chip label={`${filteredCount} Result${filteredCount !== 1 ? "s" : ""}`} sx={{ borderRadius: 2, fontWeight: 900 }} />
            {selectedCount > 0 && <Chip color="primary" label={`${selectedCount} Selected`} sx={{ borderRadius: 2, fontWeight: 900 }} />}
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            {/* Bulk dropdown */}
            {selectedCount > 0 && (
              <Box ref={bulkMenuRef} sx={{ position: "relative" }}>
                <Button
                  variant="contained"
                  onClick={() => setShowBulkActions((p) => !p)}
                  endIcon={<ChevronDown size={16} />}
                  sx={{ fontWeight: 1000, borderRadius: 2.5, textTransform: "none" }}
                >
                  {selectedCount} Selected
                </Button>

                {showBulkActions && (
                  <Paper variant="outlined" sx={{ position: "absolute", right: 0, mt: 1, width: 260, zIndex: 20, borderRadius: 3, overflow: "hidden" }}>
                    <Button
                      fullWidth
                      onClick={handleDownloadSelected}
                      startIcon={<Download size={16} />}
                      sx={{ justifyContent: "flex-start", py: 1.2, fontWeight: 900, textTransform: "none" }}
                    >
                      Download Selected ({selectedCount})
                    </Button>
                    <Divider />
                    <Button
                      fullWidth
                      color="error"
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete ${selectedCount} selected assets?`)) {
                          // placeholder (same as old)
                          setSelectedAssets([]);
                          setShowBulkActions(false);
                        }
                      }}
                      startIcon={<Trash2 size={16} />}
                      sx={{ justifyContent: "flex-start", py: 1.2, fontWeight: 900, textTransform: "none" }}
                    >
                      Delete Selected
                    </Button>
                  </Paper>
                )}
              </Box>
            )}

            <Button
              variant="outlined"
              onClick={handleExportToExcel}
              disabled={!assetsData?.length}
              startIcon={<Download size={18} />}
              sx={{ fontWeight: 900, borderRadius: 2.5, textTransform: "none" }}
            >
              Download All
            </Button>

            {assetActions.canBulkUpload && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleDownloadTemplate}
                  startIcon={<Download size={18} />}
                  sx={{ fontWeight: 900, borderRadius: 2.5, textTransform: "none" }}
                >
                  Template
                </Button>

                <input type="file" id="bulkUpload" ref={fileInputRef} accept=".xlsx,.xls,.csv" onChange={handleBulkFileChange} hidden />

                <Button
                  variant={bulkFile ? "contained" : "outlined"}
                  component="label"
                  htmlFor="bulkUpload"
                  startIcon={<Upload size={18} />}
                  sx={{ fontWeight: 1000, borderRadius: 2.5, textTransform: "none", borderStyle: bulkFile ? "solid" : "dashed" }}
                >
                  {bulkFile ? "File Selected" : "Upload Bulk"}
                </Button>

                {bulkFile && (
                  <>
                    <Tooltip title="View upload requirements">
                      <IconButton
                        onClick={() => {
                          setUploadRequirements([
                            "📋 Required File Format:",
                            "✅ File types: .xlsx, .xls, .csv",
                            "✅ Required columns: IMEI, Model, Serial Number, Type, Status, Purchase Date",
                            "✅ IMEI must be unique (15-17 digits)",
                            "✅ Purchase Date format: YYYY-MM-DD",
                            "✅ Status: active, inactive, online, offline, deleted",
                            "⚠️ Maximum 1000 records per upload",
                            "⚠️ File size limit: 10MB",
                          ]);
                          setShowUploadValidation(true);
                        }}
                      >
                        <Info size={18} />
                      </IconButton>
                    </Tooltip>

                    <Button
                      variant="contained"
                      onClick={handleEnhancedBulkUpload}
                      disabled={uploading}
                      sx={{ fontWeight: 1000, borderRadius: 2.5, textTransform: "none" }}
                    >
                      {uploading ? "Uploading..." : "Start Upload"}
                    </Button>
                  </>
                )}
              </>
            )}
          </Stack>
        </Stack>

        {uploading && uploadProgress > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="text.secondary">
              Upload progress: {uploadProgress}%
            </Typography>
          </Box>
        )}
      </Paper>

      {/* CONTENT */}
      <Paper variant="outlined" sx={{ borderRadius: 4, overflow: "hidden" }}>
        {isAssetsLoading ? (
          <Box sx={{ p: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.2 }}>
            <CircularProgress />
            <Typography color="text.secondary">Syncing fleet inventory...</Typography>
          </Box>
        ) : assetError ? (
          <Box sx={{ p: 6 }}>
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              {assetError}
            </Alert>
          </Box>
        ) : filteredAssets.length > 0 ? (
          <Box sx={{ height: isSmDown ? 680 : 720, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row?.id} // ✅ stable
              getRowHeight={() => "auto"}
              disableRowSelectionOnClick
              pageSizeOptions={[6, 10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 6, page: 0 } },
              }}
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaders": {
                  background: alpha(theme.palette.primary.main, 0.06),
                  fontWeight: 1000,
                },
                "& .MuiDataGrid-cell": {
                  py: 1.1,
                  alignItems: "start",
                },
                "& .MuiDataGrid-row": {
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                },
              }}
            />
          </Box>
        ) : (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: 40 }}>📦</Typography>
            <Typography sx={{ fontWeight: 1000, fontSize: 18, mt: 1 }}>No assets found</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.7 }}>
              Try adjusting your search filters or add new devices.
            </Typography>
            {assetActions.canBulkUpload && (
              <Button
                variant="contained"
                onClick={() => {
                  setAssetSearchQuery("");
                  setFilterStatus("all");
                }}
                startIcon={<ExternalLink size={16} />}
                sx={{ mt: 2, fontWeight: 1000, borderRadius: 2.5, textTransform: "none" }}
              >
                View All Assets
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* UPLOAD REQUIREMENTS MODAL */}
      <Dialog
        open={showUploadValidation}
        onClose={() => setShowUploadValidation(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmDown}
        PaperProps={{ sx: { borderRadius: isSmDown ? 0 : 4 } }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AlertTriangle size={22} />
              <Typography sx={{ fontWeight: 1000 }}>Upload Requirements</Typography>
            </Stack>
            <IconButton onClick={() => setShowUploadValidation(false)}>
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1}>
            {uploadRequirements.map((req, idx) => {
              const isBad = req.startsWith("❌");
              const isWarn = req.startsWith("⚠️");
              const color = isBad ? "error.main" : isWarn ? "warning.main" : "success.main";
              return (
                <Typography key={idx} sx={{ color, fontWeight: 800 }}>
                  {req}
                </Typography>
              );
            })}
          </Stack>

          {!bulkFile ? (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
              Please select a file first.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 3 }}>
              Selected: <strong>{bulkFile.name}</strong>
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: "100%" }} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => setShowUploadValidation(false)} sx={{ fontWeight: 900, borderRadius: 2, textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowUploadValidation(false);
                handleEnhancedBulkUpload();
              }}
              disabled={!bulkFile}
              sx={{ fontWeight: 1000, borderRadius: 2, textTransform: "none" }}
            >
              Proceed with Upload
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AssetManagement;
